/**
 * ----------------------------------------------------------------------------
 * Nonconformity Chart Labels
 * ----------------------------------------------------------------------------
 * Summary:
 * Shared SVG labels for the nonconformity report charts. Text longer than
 * MAX_LABEL_WIDTH pixels is cut with an ellipsis, measured on the rendered
 * node so the limit is the same for every language.
 *
 * @created November-2026
 * ----------------------------------------------------------------------------
 */
import { SVGProps, useLayoutEffect, useRef } from "react"

interface TruncatedSvgTextProps extends SVGProps<SVGTextElement> {
  text: string
  // Kept in full after the truncated text and counted in the width (e.g. a count).
  suffix?: string
}

const MAX_LABEL_WIDTH = 100
const LABEL_ELLIPSIS = "..."

const truncateLabel = (node: SVGTextElement, text: string, suffix: string, maxWidth: number) => {
  const measure = (value: string) => {
    node.textContent = value
    return node.getComputedTextLength()
  }

  if (measure(text + suffix) <= maxWidth) return text + suffix

  // Binary search for the longest prefix that fits along with the ellipsis and suffix.
  const chars = Array.from(text)
  const withPrefix = (length: number) =>
    chars.slice(0, length).join("") + LABEL_ELLIPSIS + suffix
  let low = 0
  let high = chars.length - 1
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (measure(withPrefix(mid)) <= maxWidth) {
      low = mid
    } else {
      high = mid - 1
    }
  }
  return withPrefix(low)
}

export const TruncatedSvgText = (props: TruncatedSvgTextProps) => {
  const { text, suffix = "", ...textProps } = props
  const textRef = useRef<SVGTextElement>(null)

  // The label is set here rather than as a child, as measuring needs to write to the node.
  useLayoutEffect(() => {
    if (textRef.current) {
      textRef.current.textContent = truncateLabel(textRef.current, text, suffix, MAX_LABEL_WIDTH)
    }
  }, [text, suffix])

  return <text ref={textRef} {...textProps} />
}

export const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props
  return (
    <g transform={`translate(${x},${y})`}>
      <TruncatedSvgText
        text={payload?.value ?? ""}
        x={0}
        y={0}
        dy={8}
        textAnchor="start"
        transform="rotate(45)"
        className="recharts-text recharts-cartesian-axis-tick-value"
      />
    </g>
  )
}
